import React, { useState } from 'react';
import {
  Mail,
  ArrowLeft,
  User,
  Lock,
  AlertCircle,
  UserPlus,
  LogIn,
  X,
  Coffee
} from 'lucide-react';
import { AcademyConfig, UserState, saveUserSession } from '../../config/academyConfig';
import { authenticateVendor } from '../../services/myshopAuth';

interface LoginModalProps {
  config: AcademyConfig;
  onClose: () => void;
  onLogin: (userData: UserState) => void;
}

type AuthMode = 'options' | 'academy-signup' | 'academy-login' | 'vendor-login';

const LoginModal: React.FC<LoginModalProps> = ({ config, onClose, onLogin }) => {
  const [authMode, setAuthMode] = useState<AuthMode>('options');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Academy email auth (for learners, not vendors)
  const handleAcademyAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // TODO: Replace with actual API call for academy users
      // For now, simulate successful auth
      await new Promise(resolve => setTimeout(resolve, 1000));

      const userData: UserState = {
        mode: 'academy',
        isAuthenticated: true,
        email,
        name: name || email.split('@')[0],
        academyProgress: {
          enrolledAt: new Date().toISOString(),
          modulesCompleted: [],
          totalPoints: 0,
          certificateEarned: false
        }
      };

      saveUserSession('academy-token-' + Date.now(), userData);
      onLogin(userData);
    } catch (err) {
      setError('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // MyShop vendor auth (uses MyShop.coffee credentials)
  const handleVendorAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await authenticateVendor(email, password);

      if (result.success && result.token) {
        const userData: UserState = {
          mode: 'vendor',
          isAuthenticated: true,
          email: result.email,
          name: result.name,
          vendorId: String(result.vendor_id),
          storeName: result.store_name,
          avatarUrl: result.avatar_url,
        };

        saveUserSession(result.token, userData);
        onLogin(userData);
      } else {
        // Handle specific error types
        switch (result.error) {
          case 'invalid_credentials':
            setError('Invalid email or password. Please use your MyShop.coffee login credentials.');
            break;
          case 'not_vendor':
            setError('This account is not a vendor. Sign up as a vendor at myshop.coffee first.');
            break;
          case 'too_many_attempts':
            setError('Too many login attempts. Please wait 15 minutes and try again.');
            break;
          case 'network_error':
            setError('Unable to connect. Please check your internet connection.');
            break;
          default:
            setError(result.message || 'Login failed. Please try again.');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderOptions = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <img src={config.logo} alt="YourBeans" className="h-12 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-900">Welcome to Coffee Academy</h2>
        <p className="text-gray-600 text-sm mt-1">Learn. Connect. Launch your coffee business.</p>
      </div>

      {/* Academy Sign Up (for learners) */}
      <button
        onClick={() => setAuthMode('academy-signup')}
        className="w-full flex items-center justify-center p-3 rounded-lg font-medium text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: config.primaryColor }}
      >
        <Mail className="w-5 h-5 mr-2" />
        Continue with Email
      </button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">existing vendor?</span>
        </div>
      </div>

      {/* MyShop.coffee Vendor Login */}
      <button
        onClick={() => setAuthMode('vendor-login')}
        className="w-full flex items-center justify-center p-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
      >
        <Coffee className="w-5 h-5 mr-2" />
        Sign in with MyShop.coffee
      </button>
      <p className="text-xs text-gray-500 text-center">
        Use your existing MyShop.coffee vendor credentials
      </p>

      {/* Already have academy account */}
      <div className="text-center pt-4">
        <span className="text-gray-600 text-sm">Already have an academy account? </span>
        <button
          onClick={() => setAuthMode('academy-login')}
          className="text-sm font-medium hover:underline"
          style={{ color: config.primaryColor }}
        >
          Sign In
        </button>
      </div>
    </div>
  );

  const renderVendorLogin = () => (
    <div className="space-y-4">
      <button
        onClick={() => setAuthMode('options')}
        className="flex items-center text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </button>

      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <Coffee className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Vendor Sign In</h2>
        <p className="text-gray-600 text-sm mt-1">
          Use your MyShop.coffee credentials
        </p>
      </div>

      <form onSubmit={handleVendorAuth} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@myshop-email.com"
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your MyShop password"
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
            <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center p-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Signing in...
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5 mr-2" />
              Sign In as Vendor
            </>
          )}
        </button>
      </form>

      <div className="text-center pt-4 space-y-2">
        <a
          href="https://myshop.coffee/my-account/lost-password/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-gray-500 hover:underline block"
        >
          Forgot your MyShop password?
        </a>
        <p className="text-xs text-gray-400">
          Not a vendor yet?{' '}
          <a
            href="https://myshop.coffee/pricing/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-600 hover:underline"
          >
            Start your free trial
          </a>
        </p>
      </div>
    </div>
  );

  const renderAcademyForm = () => (
    <div className="space-y-4">
      <button
        onClick={() => setAuthMode('options')}
        className="flex items-center text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </button>

      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          {authMode === 'academy-signup' ? 'Create Your Account' : 'Welcome Back'}
        </h2>
        <p className="text-gray-600 text-sm mt-1">
          {authMode === 'academy-signup'
            ? 'Start your coffee business journey'
            : 'Sign in to continue learning'}
        </p>
      </div>

      <form onSubmit={handleAcademyAuth} className="space-y-4">
        {authMode === 'academy-signup' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              minLength={6}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
            <AlertCircle className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center p-3 rounded-lg font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: config.primaryColor }}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Please wait...
            </>
          ) : authMode === 'academy-signup' ? (
            <>
              <UserPlus className="w-5 h-5 mr-2" />
              Create Account
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5 mr-2" />
              Sign In
            </>
          )}
        </button>
      </form>

      <div className="text-center pt-4">
        {authMode === 'academy-signup' ? (
          <>
            <span className="text-gray-600 text-sm">Already have an account? </span>
            <button
              onClick={() => setAuthMode('academy-login')}
              className="text-sm font-medium hover:underline"
              style={{ color: config.primaryColor }}
            >
              Sign In
            </button>
          </>
        ) : (
          <>
            <span className="text-gray-600 text-sm">Don't have an account? </span>
            <button
              onClick={() => setAuthMode('academy-signup')}
              className="text-sm font-medium hover:underline"
              style={{ color: config.primaryColor }}
            >
              Sign Up
            </button>
          </>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (authMode) {
      case 'options':
        return renderOptions();
      case 'vendor-login':
        return renderVendorLogin();
      case 'academy-signup':
      case 'academy-login':
        return renderAcademyForm();
      default:
        return renderOptions();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {renderContent()}

        {/* Terms */}
        <p className="text-xs text-gray-400 text-center mt-6">
          By continuing, you agree to our{' '}
          <a href="/terms" className="underline">Terms of Service</a> and{' '}
          <a href="/privacy" className="underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
